import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaQuestionCircle,
  FaBook,
  FaEdit,
  FaCheckCircle,
  FaStar,
  FaExclamationTriangle,
  FaCalendarAlt,
} from "react-icons/fa";

import { useGetQuestionDetailsQuery } from "../../slices/questionApiSlice";

const QuestionDetailsScreen = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const { data: question, isLoading, error } = useGetQuestionDetailsQuery(id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-gray-600">
        Loading question...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="rounded-xl bg-red-100 p-5 text-red-700 shadow-lg">
          {error?.data?.message || error.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* <div className="flex justify-between items-center mb-6">
          <Link
            to="/questions"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 shadow hover:bg-blue-50 text-blue-600 transition"
          >
            ← Back to Questions
          </Link>

          <button
            onClick={() => navigate(`/questions/${question._id}/edit`)}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 font-semibold text-white shadow hover:bg-amber-600 transition"
          >
            <FaEdit />
            Edit Question
          </button>
        </div> */}

        <div className="rounded-3xl border border-white/30 bg-white/80 backdrop-blur-lg shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl bg-white/20 p-4">
                <FaQuestionCircle className="text-4xl" />
              </div>

              <div>
                <h1 className="text-3xl md:text-4xl font-bold">
                  Question Details
                </h1>

                <p className="mt-2 text-blue-100">
                  View complete question information
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Question */}

            <div>
              <label className="text-sm font-semibold uppercase text-gray-500">
                Question
              </label>

              <div className="mt-3 rounded-2xl bg-gray-50 border p-5 text-xl font-semibold text-gray-800">
                {question.questionText}
              </div>
            </div>

            {/* Chapter and Subject */}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Chapter
                </label>

                <div className="mt-3 flex items-center gap-3 text-lg font-semibold">
                  <FaBook className="text-blue-600" />

                  {question.chapter?.name || "N/A"}
                </div>
              </div>

              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Subject
                </label>

                <div className="mt-3 flex items-center gap-3 text-lg font-semibold">
                  <FaBook className="text-green-600" />

                  {question.subject?.name || "N/A"}
                </div>
              </div>
            </div>

            {/* Options */}

            <div>
              <label className="text-sm font-semibold uppercase text-gray-500">
                Options
              </label>

              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {question.options?.map((option, index) => {
                  const optionText =
                    typeof option === "object" ? option.text : option;

                  const optionKey =
                    typeof option === "object"
                      ? option.key
                      : String.fromCharCode(65 + index);

                  const isCorrect =
                    question.correctAnswer === optionText ||
                    question.correctAnswer === optionKey;

                  return (
                    <div
                      key={option._id || index}
                      className={`rounded-xl border p-4 ${
                        isCorrect
                          ? "bg-green-100 border-green-400 text-green-800"
                          : "bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-blue-600">
                          {optionKey}.
                        </span>

                        <span>{optionText}</span>

                        {isCorrect && (
                          <FaCheckCircle className="ml-auto text-green-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Remaining sections remain unchanged */}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-green-50 p-5">
                <label className="text-sm font-semibold uppercase text-green-700">
                  Correct Answer
                </label>

                <p className="mt-3 text-lg font-bold text-green-800">
                  {question.correctAnswer}
                </p>
              </div>

              <div className="rounded-xl border bg-red-50 p-5">
                <label className="text-sm font-semibold uppercase text-red-700">
                  Negative Marks
                </label>

                <p className="mt-3 flex items-center gap-2 text-lg font-bold text-red-800">
                  <FaExclamationTriangle />

                  {question.negativeMarks}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Marks
                </label>

                <p className="mt-3 flex items-center gap-2 text-lg font-bold">
                  <FaStar className="text-yellow-500" />

                  {question.marks}
                </p>
              </div>

              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Difficulty
                </label>

                <p className="mt-3 text-lg font-bold capitalize">
                  {question.difficulty}
                </p>
              </div>

              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Question Type
                </label>

                <p className="mt-3 text-lg font-bold capitalize">
                  {question.questionType}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold uppercase text-gray-500">
                Explanation
              </label>

              <div className="mt-3 rounded-xl border bg-slate-50 p-5 text-gray-700">
                {question.explanation || "No explanation provided."}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Question Order
                </label>

                <p className="mt-3 text-lg font-bold">{question.order}</p>
              </div>

              <div className="rounded-xl border bg-gray-50 p-5">
                <label className="text-sm font-semibold uppercase text-gray-500">
                  Created Date
                </label>

                <p className="mt-3 flex items-center gap-2 text-lg font-bold">
                  <FaCalendarAlt className="text-indigo-600" />

                  {new Date(question.createdAt).toLocaleString()}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold uppercase text-gray-500">
                Question ID
              </label>

              <div className="mt-2 rounded-xl bg-slate-900 p-4 font-mono text-sm text-green-400 break-all">
                {question._id}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetailsScreen;
